/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import { tracked } from '@glimmer/tracking';

import { timeout } from 'ember-concurrency';
import { task } from 'ember-concurrency';

import config from 'cio/config/environment';
import AlertsService from 'cio/services/alerts';
import AnalyticsService from 'cio/services/analytics';
import ApiErrorsService from 'cio/services/api-errors';
import CollectionModel from 'cio/models/collection';

type CollectionsTableRowArgs = {
  collection: CollectionModel;
  onImportRetry: () => unknown;
};

export default class CollectionsTableRow extends Component<CollectionsTableRowArgs> {
  @service alerts!: AlertsService;
  @service analytics!: AnalyticsService;
  @service apiErrors!: ApiErrorsService;

  @tracked showConfirmDeleteModal = false;
  @tracked showUpdateModal = false;

  get dropdownOptions() {
    return [
      {
        label: 'Update Collection',
        action: () => {
          return this.revealUpdateModal();
        },
      },
      {
        label: 'Delete Collection',
        classNames: 'text-error',
        disabled: this.args.collection.inUse,
        action: () => {
          return this.revealConfirmDeleteModal();
        },
        tooltip: {
          condition: this.args.collection.inUse,
          content: 'Collections that are used in a Campaign cannot be deleted.',
        },
      },
    ];
  }

  pollForImportStatusTask = task({ drop: true }, async (collection: CollectionModel) => {
    while (collection.importing) {
      await timeout(config.APP.importStatePollingDuration);
      await collection.reload();
    }
  });

  dismissImportErrorTask = task({ drop: true }, async (collection: CollectionModel) => {
    if (collection.importIsInvalid) {
      await collection.destroyRecord();
    } else {
      collection.set('updateType', 'clear_error');
      await collection.save();
      collection.resetUpdateType();
    }
  });

  retryImportTask = task({ drop: true }, async (collection: CollectionModel) => {
    if (collection.importIsInvalid) {
      await collection.destroyRecord();
      this.args.onImportRetry();
    } else {
      this.showUpdateModal = true;
    }
  });

  deleteCollectionTask = task({ drop: true }, async (collection: CollectionModel) => {
    try {
      await collection.destroyRecord();
      this.alerts.success('collection.create.success', `${collection.name} deleted`);
    } catch (error) {
      this.apiErrors.report(error);
    }

    this.showConfirmDeleteModal = false;
  });

  updateCollectionTask = task({ drop: true }, async (collection: CollectionModel) => {
    try {
      await collection.save();
      this.pollForImportStatusTask.perform(collection);
    } catch (error) {
      this.apiErrors.report(error);
    }

    this.showUpdateModal = false;
  });

  @action
  revealConfirmDeleteModal() {
    this.showConfirmDeleteModal = true;
    this.analytics.track('collections', 'delete_started', { model: this.args.collection });
  }

  @action
  revealUpdateModal() {
    this.showUpdateModal = true;
    this.analytics.track('collections', 'update_started', { model: this.args.collection });
  }

  constructor(owner: unknown, args: CollectionsTableRowArgs) {
    super(owner, args);

    let { collection } = this.args;

    if (collection.importing) {
      this.pollForImportStatusTask.perform(collection);
    }
  }
}
